// Tabs and overlays live in the URL (/trip?o=place:otaru-canal) so reloads and deep
// links restore them. Android back (HANDOVER §3): closes the top overlay first; from
// any tab goes to Map; on Map shows "Press back again to close".
//
// History, bottom to top: Map root, Map guard, the tab (when it isn't Map; tab → tab
// replaces it), then one entry per open overlay. An overlay opened from an overlay is
// pushed, so ✕ and Back walk back one level (Settings › My rules › Back → Settings).
import { useSyncExternalStore } from 'react'

/** Learn is the 5th tab (provisional, BUILD_PLAN_NEXT §4.1): where plays are earned; the Arcade is where they're spent. */
export const TABS = ['map', 'trip', 'learn', 'arcade', 'crate'] as const
/** A grown-up's dock (WI-19): the five tabs and Organiser home, the parent's landing. */
export const GROWN_UP_TABS = [...TABS, 'organiser'] as const
/**
 * Pages reached from somewhere else, not from the dock: the organiser's itinerary editor, the
 * install and privacy pages (open before sign-in too), and join (a phone not yet linked to a
 * family; opened as /join#CODE from an invite).
 */
export const PAGES = [...GROWN_UP_TABS, 'plan', 'install', 'privacy', 'join'] as const
export type Tab = (typeof PAGES)[number]
export type DockTab = (typeof GROWN_UP_TABS)[number]

export interface Route {
  tab: Tab
  /** Overlay key, e.g. 'settings', 'place:otaru-canal', 'stage:arrival'. */
  overlay: string | null
}

export function parse(url: URL): Route {
  const seg = url.pathname.replace(/^\/+/, '').split('/')[0] ?? ''
  const tab = (PAGES as readonly string[]).includes(seg) ? (seg as Tab) : 'map'
  return { tab, overlay: url.searchParams.get('o') }
}

export function format(r: Route): string {
  return `/${r.tab}${r.overlay ? `?o=${encodeURIComponent(r.overlay)}` : ''}`
}

/** `under`: on an overlay's entry, the overlay it was opened from (null: straight from the tab). */
type HistoryState = { tq: 'root' | 'guard' | 'nav'; under?: string | null }

const MAP: Route = { tab: 'map', overlay: null }
let current: Route = MAP
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

let exitArmedAt = 0
let onExitHint: (() => void) | null = null
/** Where an unlinked phone was headed before it was shown /join; restored once it's linked. */
let afterJoin: Route | null = null

const historyState = () => (history.state ?? null) as HistoryState | null

function set(r: Route, mode: 'push' | 'replace', under: string | null = null) {
  current = r
  const state: HistoryState = { tq: 'nav', under }
  if (mode === 'push') history.pushState(state, '', format(r))
  else history.replaceState(state, '', format(r))
  emit()
}

/** Call once at startup. */
export function initRouter(opts: { onExitHint(): void }) {
  onExitHint = opts.onExitHint
  exitArmedAt = 0
  pendingTab = null
  pendingThen = null
  const start = parse(new URL(location.href))
  // A Map "root" entry with a guard above it, so the first Back on Map can be caught.
  // Opened as /join: the guard shows /join, and the app goes on to the Map once linked.
  const joining = start.tab === 'join'
  afterJoin = joining ? MAP : null
  history.replaceState({ tq: 'root' } satisfies HistoryState, '', format(MAP))
  history.pushState({ tq: 'guard' } satisfies HistoryState, '', format(joining ? { tab: 'join', overlay: null } : MAP))
  if (!joining) {
    if (start.tab !== 'map') history.pushState({ tq: 'nav' } satisfies HistoryState, '', format({ tab: start.tab, overlay: null }))
    if (start.overlay) history.pushState({ tq: 'nav', under: null } satisfies HistoryState, '', format(start))
  }
  current = joining ? { tab: 'join', overlay: null } : start
  window.removeEventListener('popstate', onPop)
  window.addEventListener('popstate', onPop)
}

function onPop(e: PopStateEvent) {
  const state = e.state as HistoryState | null
  if (state?.tq === 'root') {
    // Back pressed on the bare Map.
    pendingTab = null
    if (Date.now() - exitArmedAt < 2000) {
      history.back() // leave the app
      return
    }
    exitArmedAt = Date.now()
    onExitHint?.()
    history.pushState({ tq: 'guard' } satisfies HistoryState, '', format(MAP))
    current = MAP
    emit()
    return
  }
  current = parse(new URL(location.href))
  if (pendingTab) {
    // Still overlays on the way down to the tab: keep going without showing each one.
    if (current.overlay) {
      history.back()
      return
    }
    emit()
    const tab = pendingTab
    const then = pendingThen ?? undefined
    pendingTab = null
    pendingThen = null
    goTab(tab, then)
    return
  }
  emit()
  if (pendingThen && !current.overlay) {
    const then = pendingThen
    pendingThen = null
    openOverlay(then)
  }
}

/** Calls `fn` after every route change (outside React; B3's lock decides there). */
export function onRouteChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export const currentRoute = (): Route => current

export function useRoute(): Route {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => current,
  )
}

let pendingTab: Tab | null = null
/** The overlay to open once a goTab(tab, then) lands (WI-42: a checklist step into /plan's Stays). */
let pendingThen: string | null = null

/** Goes to a tab (closing any overlays first), then opens `then` on it when given. */
export function goTab(tab: Tab, then?: string) {
  if (current.overlay) {
    // Close every open overlay first; the popstate handler then finishes the move.
    pendingTab = tab
    pendingThen = then ?? null
    history.back()
    return
  }
  if (tab !== current.tab) {
    if (tab === 'map') {
      history.back() // tabs sit one entry above Map
      if (then) pendingThen = then
      return
    }
    set({ tab, overlay: null }, current.tab === 'map' ? 'push' : 'replace')
  }
  if (then) openOverlay(then)
}

/** Opens an overlay on top of what's showing (a tab, or another overlay): Back and ✕ return there. */
export function openOverlay(overlay: string) {
  if (overlay === current.overlay) return
  set({ tab: current.tab, overlay }, 'push', current.overlay)
}

/**
 * Swaps the open overlay for another, so Back skips the first: a finished round handing over to
 * its reward, the reward to My prizes. With no overlay open it opens one.
 */
export function replaceOverlay(overlay: string) {
  if (!current.overlay) return openOverlay(overlay)
  if (overlay === current.overlay) return
  set({ tab: current.tab, overlay }, 'replace', historyState()?.under ?? null)
}

/** Closes the top overlay: back to the overlay it was opened from, or to the tab. */
export function closeOverlay() {
  if (!current.overlay) return
  history.back()
}

/**
 * Back to `overlay` when the open one was opened from it (e.g. New trip → Trips); otherwise (a
 * deep link) swaps to it, so the sheet still lands where the parent expects.
 */
export function backTo(overlay: string) {
  if (overlay === current.overlay) return
  if (current.overlay && historyState()?.under === overlay) history.back()
  else replaceOverlay(overlay)
}

/** Closes every open overlay, down to the tab (e.g. before another person takes the phone). */
export function closeAllOverlays() {
  if (current.overlay) goTab(current.tab)
}

/**
 * A phone that isn't linked to a family shows /join (the join screen) wherever it was headed;
 * once it's linked, it goes on to the route first asked for. Replaces the current entry both ways.
 */
export function showJoin(on: boolean) {
  if (on === (current.tab === 'join')) return
  if (on) {
    afterJoin ??= current
    current = { tab: 'join', overlay: null }
  } else {
    current = afterJoin ?? MAP
    afterJoin = null
  }
  history.replaceState(historyState(), '', format(current))
  emit()
}

/**
 * Where the app should open next time a person's app shell mounts (WI-19: a parent who has just
 * started a family lands on Organiser home). Taken once by `takeLanding`.
 */
let landing: Tab | null = null
export function landOn(tab: Tab | null) {
  landing = tab
}
export function takeLanding(): Tab | null {
  const tab = landing
  landing = null
  return tab
}

/** For tests. */
export function _routeForTest(): Route {
  return current
}
